"use client"

import React, { useEffect, useRef } from "react"
import {
  colorSettingDetection,
  darkTextureWrapper,
  renderHeaderConditionally,
} from "../../utils/functions"
import { CommonDescription } from "../../component/CommonDescription/CommonDescription"
import {
  layoutHeaderClasses,
  layoutHeaderBigClasses,
  layoutSubheaderClasses,
  layoutEyebrowClasses,
} from "@/components/tailwindHelpers"

export type LayoutDangerousHTMLEmbedProps = {
  eyebrow?: string
  header?: string
  headerElement?: "h1" | "h2"
  subheader?: string
  description?: string
  htmlBlob: string
  aspectRatio?: string
  anchor?: string
  presetTheme?:
    | "t2-theme-one"
    | "t2-theme-two"
    | "t2-theme-three"
    | "t2-theme-four"
    | "t2-theme-five"
    | "t2-theme-six"
    | "t2-theme-seven"
  isHeaderBig?: boolean
  isWrapped?: boolean
}

export const LayoutDangerousHTMLEmbed: React.FC<
  LayoutDangerousHTMLEmbedProps
> = ({
  eyebrow,
  header,
  headerElement = "h2",
  subheader,
  description,
  htmlBlob,
  aspectRatio,
  anchor,
  presetTheme,
  isHeaderBig,
  isWrapped,
}) => {
  const embedRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = embedRef.current
    // Guard against React Strict Mode's double-invoke re-running this and
    // tearing out a script mid-flight (its async continuation then errors
    // trying to touch a detached element).
    if (!container || container.dataset.scriptsReexecuted === "true") return
    container.dataset.scriptsReexecuted = "true"

    // Scripts created via document.createElement run asynchronously by
    // default, even without the `async` attribute, so simply replacing
    // them all in a forEach lets an inline script (e.g. hbspt.forms.create)
    // fire before a preceding external <script src> has finished loading.
    // Run them sequentially instead, awaiting each external script's
    // "load" event before moving on to the next <script> tag, to mimic
    // native parser behavior.
    let cancelled = false

    const runScriptsSequentially = async () => {
      const scripts = Array.from(container.querySelectorAll("script"))

      for (const oldScript of scripts) {
        if (cancelled) return

        const newScript = document.createElement("script")
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value)
        })
        newScript.textContent = oldScript.textContent

        const hasSrc = !!oldScript.getAttribute("src")

        const loadPromise = hasSrc
          ? new Promise<void>((resolve) => {
              newScript.addEventListener("load", () => resolve(), {
                once: true,
              })
              newScript.addEventListener("error", () => resolve(), {
                once: true,
              })
            })
          : Promise.resolve()

        oldScript.parentNode?.replaceChild(newScript, oldScript)

        // Inline scripts execute synchronously as soon as they're
        // inserted, so this only actually waits for external ones.
        await loadPromise
      }
    }

    runScriptsSequentially()

    return () => {
      cancelled = true
    }
  }, [htmlBlob])

  const wrapperClasses = `${isWrapped ? "w-full px-5 lg:px-20 m-auto max-w-[1440px]" : ""}`
  const colorSetting = colorSettingDetection(presetTheme || "")
  const mainClasses = `t2-layout-dangerous-html-embed py-24 px-5 ${presetTheme || ""} ${colorSetting} `
  const headerClasses = `t2-layout-dangerous-html-embed__header ${isHeaderBig ? layoutHeaderBigClasses : layoutHeaderClasses}
`
  const finalHeader = renderHeaderConditionally(
    headerClasses,
    header || "",
    headerElement,
  )

  const componentStyles = darkTextureWrapper(presetTheme || "")

  return (
    <div
      className={mainClasses}
      style={componentStyles}
      id={anchor || ""}
      data-testid="layout-html-embed"
    >
      <div className={wrapperClasses}>
        <div className="t2-layout-dangerous-html-embed__content t2-layout__content">
          <div className="pb-20">
            <div className="t2-header-area">
              {eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>}
              {finalHeader}
              {subheader && (
                <div className={layoutSubheaderClasses}>{subheader}</div>
              )}
              {description && (
                <CommonDescription
                  description={description}
                  className={
                    "t2-layout-dangerous-html-embed__description t2-layout__description"
                  }
                />
              )}
            </div>
          </div>
          <div
            className="w-full h-full block"
            style={{ aspectRatio: aspectRatio || "auto" }}
            dangerouslySetInnerHTML={{ __html: htmlBlob }}
            ref={embedRef}
          />
        </div>
      </div>
    </div>
  )
}
